import { Typography } from '@mui/material';
import { useContext, useState } from 'react';

import { StatusCode } from '../../../api/enumerations';
import { resetPasswordProto } from '../../../api/resetPassword';
import { IconVisibility, PasswordIcon } from '../../../components/InputIcon';
import { DialogTitle } from '../../../components/UI/Typography';
import { IconEditMS } from '../../../constants/icons';
import { GlobalContext } from '../../../context/global';
import { validatePassword } from '../../../helpers';
import useErrorMessage from '../../../hooks/useErrorMessage';
import {
  ButtonBox,
  DialogContainer,
  FilledDarkButton,
  OutlinedDarkButton,
  StyledDialog,
  StyledTextField,
} from './styles';

interface ResetPasswordProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export function ResetPassword({
  open,
  setOpen,
}: ResetPasswordProps): JSX.Element {
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmationError, setConfirmationError] = useState(false);

  const { setErrorMessage, setOpenSnackbar, setSnackbarMessage } =
    useContext(GlobalContext);

  const { getErrorMessage } = useErrorMessage();

  const handleClose = (): void => {
    setOpen(false);
    setCurrentPassword('');
    setPassword('');
    setPasswordConfirmation('');
    setPasswordError(false);
    setConfirmationError(false);
    setShowCurrentPassword(false);
    setShowPassword(false);
    setShowConfirmation(false);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!currentPassword || !password || !passwordConfirmation) {
      setSnackbarMessage('Preencha todos os campos!');
      setErrorMessage(true);
      setOpenSnackbar(true);
      return;
    }

    if (password !== passwordConfirmation) {
      setSnackbarMessage('Confirme sua senha.');
      setErrorMessage(true);
      setOpenSnackbar(true);
      return;
    }

    const passwordData = {
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    };
    try {
      const response = await resetPasswordProto(passwordData);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setSnackbarMessage('Senha alterada com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      handleClose();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      aria-labelledby="reset password dialog"
    >
      <DialogContainer>
        <DialogTitle>
          {IconEditMS}
          Alterar Senha
        </DialogTitle>
        <StyledTextField
          required
          type={showCurrentPassword ? 'text' : 'password'}
          InputProps={{
            startAdornment: <PasswordIcon color="#8B8C8E" />,
            endAdornment: (
              <IconVisibility
                showPassword={showCurrentPassword}
                setShowPassword={setShowCurrentPassword}
                color="#8B8C8E"
              />
            ),
          }}
          id="current-password"
          placeholder="digite sua senha atual"
          value={currentPassword}
          onChange={(e) => {
            setCurrentPassword(e.target.value);
          }}
          variant="standard"
        />
        <Typography my={4} textAlign="center">
          Escolha uma nova senha
        </Typography>
        <StyledTextField
          required
          type={showPassword ? 'text' : 'password'}
          helperText={
            passwordError &&
            'mínimo de 8 caracteres com letra mínuscula, maiúscula e número'
          }
          InputProps={{
            startAdornment: <PasswordIcon color="#8B8C8E" />,
            endAdornment: (
              <IconVisibility
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                color="#8B8C8E"
              />
            ),
          }}
          id="password"
          placeholder="digite uma nova senha"
          error={passwordError}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            !validatePassword(e.target.value)
              ? setPasswordError(true)
              : setPasswordError(false);
          }}
          variant="standard"
        />
        <StyledTextField
          required
          type={showConfirmation ? 'text' : 'password'}
          helperText={confirmationError && 'as senhas devem ser iguais'}
          InputProps={{
            startAdornment: <PasswordIcon color="#8B8C8E" />,
            endAdornment: (
              <IconVisibility
                showPassword={showConfirmation}
                setShowPassword={setShowConfirmation}
                color="#8B8C8E"
              />
            ),
          }}
          id="confirmation"
          placeholder="confirme sua senha"
          error={confirmationError}
          value={passwordConfirmation}
          onChange={(e) => {
            setPasswordConfirmation(e.target.value);
            e.target.value !== password
              ? setConfirmationError(true)
              : setConfirmationError(false);
          }}
          variant="standard"
        />
        <ButtonBox>
          <OutlinedDarkButton onClick={handleClose}>
            cancelar
          </OutlinedDarkButton>
          <FilledDarkButton onClick={handleSubmit}>alterar</FilledDarkButton>
        </ButtonBox>
      </DialogContainer>
    </StyledDialog>
  );
}
